import { CreateResourceParams, queryKeys } from "./ReactQuery/reactQueryService";
import { apiPost } from "./Api/apiCall";
import { QueryClient, UseMutationResult } from "@tanstack/react-query";
import React from "react";
import { ProductUnion } from "../types/ProductUnion";
import { BackofficeUser, Roles } from "../types/BackofficeUser";
import { userHasOneOfTheseRoles } from "./backofficeUserService";
import { AiChatAgent, AiChatThread } from "../types/AIChat/AiChatThread";
import ApiClient from "./ApiClient";

interface AiChatVoiceTranscriptionResponse {
  transcript: string;
}

export const transcribeAiChatVoiceRecording = (audio: Blob): Promise<AiChatVoiceTranscriptionResponse> => {
  const formData = new FormData();
  formData.append("audio", audio, "voice-recording.webm");

  return ApiClient.post("ai_chat/voice_transcriptions", { body: formData });
};

export const sendAiChatPrompt = async (
  product: ProductUnion,
  activeThreadId: number | null,
  setActiveThreadId: React.Dispatch<React.SetStateAction<number | null>>,
  createMutation: UseMutationResult<any, unknown, CreateResourceParams>,
  queryClient: QueryClient,
  connect: Function,
  reset: Function,
  prompt: string,
  setIsLoading: Function,
  agentType: AiChatAgent,
  activeExecutionStepId: number | null = null,
) => {
  setIsLoading(true);

  try {
    let currentThreadId = activeThreadId;
    if (!activeThreadId) {
      const thread: AiChatThread = await createMutation.mutateAsync({
        uri: "ai_chat_threads",
        data: {
          backofficeCase: product.backofficeCase["@id"],
          agent: agentType,
          title: prompt,
        },
      });
      currentThreadId = thread.id;
      setActiveThreadId(currentThreadId);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("ai_chat_threads", { backofficeCase: product.backofficeCase["@id"] }),
      });
    }

    const streamId = connect();

    const messagesUri = "ai_chat_messages";

    await apiPost(messagesUri, {
      content: prompt,
      aiChatThread: "/ai_chat_threads/" + currentThreadId,
      streamId,
      ...(activeExecutionStepId !== null && { activeExecutionStepId, mode: "execution_step_edit" }),
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection(messagesUri, { aiChatThread: currentThreadId }),
    });
  } catch (error) {
    reset();
    setIsLoading(false);
    throw error;
  }
};

export const getAiAgentByUser = (user: BackofficeUser) => {
  //temporary code
  if (import.meta.env.VITE_DEPLOYMENT === "production") {
    switch (true) {
      case userHasOneOfTheseRoles(user, [Roles.admin, Roles.productManager]):
        return "LEXA";
      default:
        return null;
    }
  }

  switch (true) {
    case userHasOneOfTheseRoles(user, [
      Roles.admin,
      Roles.traineeLawyer,
      Roles.lawyer,
      Roles.leadLawyer,
      Roles.developer,
      Roles.productManager,
      Roles.banking,
    ]):
      return "LEXA";
    case userHasOneOfTheseRoles(user, [
      Roles.accountmanager,
      Roles.leadAccountmanager,
      Roles.basicAccounting,
      Roles.accounting,
    ]):
      return "LIA";
    default:
      return null;
  }
};
